import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Spinner } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import classNames from '../../../utils/classNames';
import {
  COVER_LABELS, DEFAULT_MAP_FACING, DEFAULT_MAP_SHAPE, LAYOUT_LIMITS, MAP_FACINGS, MAP_LIGHTING, MAP_OBJECTS, MAP_SHAPES, OPEN_TENTS, OPEN_TENT_SPAN, SKETCH_STYLE, STICKERS, TERRAIN, WALL_KINDS,
  brushCells, cellKey, edgesAlongLine, emptyLayout, eraseCells, fillCells, groundLayout, groundSignature, normalizeLayout, objectFacing, objectGroups, objectSketchColor, paintCells,
  placeSticker, removeSticker, setObject, setWalls, snapStickerCenter, stickerArtId, stickerAt, toggleOpenTent, trailStep, updateSticker,
} from '../../../shared/mapLayout.mjs';
import { layoutSketchDataUrl } from '../utils/mapSketch';
import { composeMapImage } from '../utils/mapOverlay';
import resolveMapImageSource from '../utils/mapImages';
import MAP_ART from '../../../shared/mapArtCatalog.json';
import MapStampPicker from './MapStampPicker';
import useTokenRotationShortcuts from '../hooks/useTokenRotationShortcuts';

// The wall sets in the art catalog, which the wall tool picks between.
const WALL_STYLES = Object.keys(MAP_ART['wall-sets'] || {});

const TOOLS = [
  { id: 'paint', label: 'Paint', hint: 'Drag to paint squares with the chosen terrain.' },
  { id: 'fill', label: 'Fill', hint: 'Click to fill the connected area of the same terrain (walls stop it).' },
  { id: 'erase', label: 'Erase', hint: 'Drag to clear terrain, cover and the walls around squares.' },
  { id: 'wall', label: 'Walls', hint: 'Drag between grid corners to draw a straight wall along the lines.' },
  { id: 'cover', label: 'Cover', hint: 'Click a square to place the chosen stamp; click it again to remove it.' },
  { id: 'scenery', label: 'Scenery', hint: 'Click a square to place the chosen stamp; click it again to remove it.' },
  { id: 'pin', label: 'Pins', hint: 'Click to drop a pin and describe what is there. The generator sees each noted pin as a numbered circle in its sketch; pins never appear on the finished map.' },
];
const HISTORY_LIMIT = 100;
// How far a sticker turns per key press or button, in degrees, as a unit's token does by its keys.
const STICKER_TURN_STEP = 15;
const STICKER_HINT = 'Click to place it; drag one to move it. Turn the chosen one with Q and E, the arrow keys or the slider; Escape lets go.';
const newStickerId = () => `sticker-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const newDraftId = () => (window.crypto?.randomUUID?.() || `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

/**
 * The DM's map maker: draw a layout on the map grid (terrain, walls, cover, pinned notes) and save it as a draft for
 * the AI map generator. Drafts live on the server per campaign (see server/utils/mapDrafts.js).
 */
export default function MapMaker({ campaignId, onClose, onGenerated }) {
  // The draft and its undo history in one state, so every change is a single pure update.
  const [editor, setEditor] = useState(() => ({
    draft: { draftId: newDraftId(), name: 'Untitled map', revision: 0, layout: emptyLayout() }, past: [], future: [],
  }));
  const { draft } = editor;
  const setDraft = useCallback((update) => setEditor((state) => ({ ...state, draft: typeof update === 'function' ? update(state.draft) : update })), []);
  const resetEditor = useCallback((nextDraft) => setEditor({ draft: nextDraft, past: [], future: [] }), []);
  const [tool, setTool] = useState('paint');
  const [terrainId, setTerrainId] = useState('grass');
  const [brushSize, setBrushSize] = useState(1);
  const [wallKind, setWallKind] = useState('wall');
  const [wallStyle, setWallStyle] = useState(WALL_STYLES[0] || '');
  // Cover and Scenery each remember their own stamp.
  const [stampIds, setStampIds] = useState({ cover: 'crates', scenery: 'tent-brown' });
  const stamping = tool === 'cover' || tool === 'scenery';
  const objectId = stampIds[stamping ? tool : 'cover'];
  const setObjectId = useCallback((id) => setStampIds((ids) => ({ ...ids, [tool]: id })), [tool]);
  const [objectSize, setObjectSize] = useState(2);
  const [facing, setFacing] = useState(DEFAULT_MAP_FACING);
  const [selectedPinId, setSelectedPinId] = useState(null);
  // The sticker being moved and turned, and the turn the next one starts at.
  const [selectedStickerId, setSelectedStickerId] = useState(null);
  const [stickerTurn, setStickerTurn] = useState(0);
  const stickerDrag = useRef(null);
  const [wallPreview, setWallPreview] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState(null);
  const svgRef = useRef(null);
  const stroke = useRef(null);
  const encodedCampaign = encodeURIComponent(campaignId || '');
  const { layout } = draft;
  const chosenStamp = MAP_OBJECTS[objectId] || OPEN_TENTS[objectId] || STICKERS[objectId];
  const placingStickers = tool === 'scenery' && Boolean(STICKERS[objectId]);
  const selectedSticker = placingStickers ? (layout.stickers || []).find(({ id }) => id === selectedStickerId) || null : null;

  // `record: false` folds a change into the previous undo step (the rest of a brush stroke, typing a note).
  const change = useCallback((next, { record = true } = {}) => {
    setEditor((state) => {
      const layoutNext = typeof next === 'function' ? next(state.draft.layout) : next;
      if (layoutNext === state.draft.layout) return state;
      return { draft: { ...state.draft, layout: layoutNext },
        past: record ? [...state.past, state.draft.layout].slice(-HISTORY_LIMIT) : state.past, future: record ? [] : state.future };
    });
    setDirty(true);
  }, []);
  const undo = useCallback(() => {
    setEditor((state) => (state.past.length ? { draft: { ...state.draft, layout: state.past[state.past.length - 1] },
      past: state.past.slice(0, -1), future: [state.draft.layout, ...state.future] } : state));
    setDirty(true);
  }, []);
  const redo = useCallback(() => {
    setEditor((state) => (state.future.length ? { draft: { ...state.draft, layout: state.future[0] },
      past: [...state.past, state.draft.layout], future: state.future.slice(1) } : state));
    setDirty(true);
  }, []);

  // Turns a sticker to an angle, lined up with the squares again if it now lies square to them.
  const turnSticker = useCallback((id, turn) => {
    const whole = ((Math.round(turn) % 360) + 360) % 360;
    setStickerTurn(whole);
    change((current) => {
      const sticker = (current.stickers || []).find((entry) => entry.id === id);
      return sticker ? updateSticker(current, id, { turn: whole, ...snapStickerCenter(current, sticker.kind, sticker, whole) }) : current;
    });
  }, [change]);
  const turnStickerBy = useCallback((id, step) => {
    const sticker = (layout.stickers || []).find((entry) => entry.id === id);
    if (sticker) turnSticker(id, sticker.turn + step);
  }, [layout.stickers, turnSticker]);
  const letGoOfSticker = useCallback(() => setSelectedStickerId(null), []);
  useTokenRotationShortcuts(selectedSticker?.id, turnStickerBy, letGoOfSticker, STICKER_TURN_STEP);
  const nextPicture = (sticker) => {
    const count = MAP_ART[stickerArtId(sticker.kind)]?.plain?.length || 0;
    change((current) => updateSticker(current, sticker.id, { art: count ? (sticker.art + 1) % count : sticker.art + 1 }));
  };
  const deleteSticker = (id) => { change((current) => removeSticker(current, id)); setSelectedStickerId(null); };

  useEffect(() => {
    const onKey = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.target?.closest?.('input, textarea')) return;
      if (event.key.toLowerCase() === 'z') { event.preventDefault(); if (event.shiftKey) redo(); else undo(); }
      if (event.key.toLowerCase() === 'y') { event.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const loadDrafts = useCallback(async () => {
    if (!campaignId) return;
    try {
      const response = await apiFetch(`/campaigns/${encodedCampaign}/map-drafts`);
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'Map drafts could not be loaded.');
      setDrafts(body.drafts || []);
    } catch (error) { setMessage({ type: 'error', text: error.message }); }
  }, [campaignId, encodedCampaign]);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  // Grid coordinates (in squares) of a pointer event over the canvas.
  const pointAt = (event) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return null;
    return { x: ((event.clientX - rect.left) / rect.width) * layout.columns, y: ((event.clientY - rect.top) / rect.height) * layout.rows };
  };
  const cellAt = (point) => ({ x: Math.min(layout.columns - 1, Math.max(0, Math.floor(point.x))), y: Math.min(layout.rows - 1, Math.max(0, Math.floor(point.y))) });
  const cornerAt = (point) => ({ x: Math.round(point.x), y: Math.round(point.y) });

  const strokeAt = (point, first) => {
    const cell = cellAt(point);
    const key = cellKey(cell.x, cell.y);
    if (!first && stroke.current?.lastKey === key) return;
    if (stroke.current) stroke.current.lastKey = key;
    const cells = brushCells(layout, cell.x, cell.y, brushSize);
    change((current) => (tool === 'erase' ? eraseCells(current, cells) : paintCells(current, cells, terrainId)), { record: first });
  };

  const onPointerDown = (event) => {
    const point = pointAt(event);
    if (!point || event.button > 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (tool === 'paint' || tool === 'erase') { stroke.current = { lastKey: null }; strokeAt(point, true); return; }
    if (tool === 'fill') {
      const cell = cellAt(point);
      change((current) => paintCells(current, fillCells(current, cell.x, cell.y), terrainId));
      return;
    }
    if (tool === 'wall') { const corner = cornerAt(point); setWallPreview({ from: corner, to: corner }); return; }
    if (placingStickers) {
      // Pressing on a sticker chooses it to drag and turn; pressing anywhere else places a new one there.
      const hit = stickerAt(layout, point);
      if (hit) {
        setSelectedStickerId(hit.id);
        stickerDrag.current = { id: hit.id, dx: hit.x - point.x, dy: hit.y - point.y, moved: false };
        return;
      }
      if ((layout.stickers || []).length >= LAYOUT_LIMITS.stickers) { setMessage({ type: 'error', text: `A map can have up to ${LAYOUT_LIMITS.stickers} stickers.` }); return; }
      // Each new sticker of a kind takes the next picture, so a row of cars isn't one car repeated.
      const sticker = { id: newStickerId(), kind: objectId, ...snapStickerCenter(layout, objectId, point, stickerTurn), turn: stickerTurn,
        art: (layout.stickers || []).filter(({ kind }) => kind === objectId).length };
      change((current) => placeSticker(current, sticker));
      setSelectedStickerId(sticker.id);
      return;
    }
    if (stamping) {
      const cell = cellAt(point);
      if (OPEN_TENTS[objectId]) { change((current) => toggleOpenTent(current, cell.x, cell.y, objectId, facing)); return; }
      const span = MAP_OBJECTS[objectId].stampSpan || 1;
      const [left, top] = [Math.min(cell.x, layout.columns - span), Math.min(cell.y, layout.rows - span)];
      const squares = Array.from({ length: span * span }, (_, index) => ({ x: left + (index % span), y: top + Math.floor(index / span) }));
      change((current) => {
        // Clicking a stamp takes it away, unless the click is to turn it.
        const [first] = squares;
        const turning = MAP_OBJECTS[objectId].facings && !MAP_OBJECTS[objectId].trail && objectFacing(current, first.x, first.y) !== facing;
        const stamp = current.objects[cellKey(first.x, first.y)] === objectId && !turning ? null : objectId;
        const center = MAP_OBJECTS[objectId].stampCenter;
        return squares.reduce((next, { x, y }, index) => setObject(next, x, y, stamp && center && index === (squares.length - 1) / 2 ? center : stamp, objectSize, facing), current);
      });
      return;
    }
    if (tool === 'pin') {
      if (layout.pins.length >= LAYOUT_LIMITS.pins) { setMessage({ type: 'error', text: `A map can have up to ${LAYOUT_LIMITS.pins} pins.` }); return; }
      const pin = { id: `pin-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, x: Math.round(point.x * 10) / 10, y: Math.round(point.y * 10) / 10, note: '' };
      change((current) => ({ ...current, pins: [...current.pins, pin] }));
      setSelectedPinId(pin.id);
    }
  };
  const onPointerMove = (event) => {
    const point = pointAt(event);
    if (!point) return;
    if (stroke.current && (tool === 'paint' || tool === 'erase')) strokeAt(point, false);
    if (wallPreview) setWallPreview((current) => ({ ...current, to: cornerAt(point) }));
    const drag = stickerDrag.current;
    const sticker = drag && (layout.stickers || []).find(({ id }) => id === drag.id);
    if (sticker) {
      const at = snapStickerCenter(layout, sticker.kind, { x: point.x + drag.dx, y: point.y + drag.dy }, sticker.turn);
      if (at.x === sticker.x && at.y === sticker.y) return;
      // One drag is one undo step.
      change((current) => updateSticker(current, drag.id, at), { record: !drag.moved });
      drag.moved = true;
    }
  };
  const onPointerUp = () => {
    stroke.current = null;
    stickerDrag.current = null;
    if (wallPreview) {
      const edges = edgesAlongLine(layout, wallPreview.from, wallPreview.to);
      if (edges.length) change((current) => setWalls(current, edges, wallKind === 'remove' ? null : wallKind, wallStyle));
      setWallPreview(null);
    }
  };

  const previewEdges = useMemo(() => (wallPreview ? edgesAlongLine(layout, wallPreview.from, wallPreview.to) : []), [layout, wallPreview]);
  const pinNumber = (id) => layout.pins.findIndex((pin) => pin.id === id) + 1;
  const selectedPin = layout.pins.find((pin) => pin.id === selectedPinId) || null;
  const updatePin = (id, patch) => change((current) => ({ ...current, pins: current.pins.map((pin) => (pin.id === id ? { ...pin, ...patch } : pin)) }), { record: false });
  const removePin = (id) => { change((current) => ({ ...current, pins: current.pins.filter((pin) => pin.id !== id) })); setSelectedPinId(null); };
  const setShape = (shape) => change((current) => normalizeLayout({ ...current, shape }));

  // Saves the draft as it is now and returns the saved revision.
  const persist = async () => {
    const response = await apiFetch(`/campaigns/${encodedCampaign}/map-drafts/${encodeURIComponent(draft.draftId)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: draft.name, layout, expectedRevision: draft.revision }),
    });
    const body = await response.json();
    if (!response.ok) throw new Error(body.message || 'The draft could not be saved.');
    setDraft((current) => ({ ...current, revision: body.revision, name: body.name }));
    setDirty(false);
    loadDrafts();
    return body.revision;
  };
  const save = async () => {
    setBusy(true); setMessage(null);
    try { await persist(); setMessage({ type: 'ok', text: 'Draft saved.' }); }
    catch (error) { setMessage({ type: 'error', text: error.message }); } finally { setBusy(false); }
  };
  // Draws the roofs, stamps and walls over a painted ground and hands back the finished map.
  const finishMap = async (painted) => {
    const image = await composeMapImage(resolveMapImageSource(painted), painted.layout);
    const { imageUrl: _ground, ...map } = painted;
    setDraft((current) => ({ ...current, groundSignature: map.groundSignature }));
    // A browser that can't write WebP gives a PNG, so the type comes from the image itself.
    const [header, imageBase64] = image.split(',');
    onGenerated?.({ ...map, imageBase64, imageType: header.slice('data:'.length, header.indexOf(';')) });
  };
  // Saves if needed (the server paints from the saved layout), then sends the ground sketch drawn from the same layout.
  const generate = async () => {
    setBusy(true); setGenerating(true); setMessage(null);
    try {
      const revision = dirty || !draft.revision ? await persist() : draft.revision;
      const response = await apiFetch(`/campaigns/${encodedCampaign}/map-drafts/${encodeURIComponent(draft.draftId)}/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sketch: layoutSketchDataUrl(groundLayout(layout)), expectedRevision: revision }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'The map could not be generated.');
      await finishMap(body);
      setMessage({ type: 'ok', text: 'Map generated. Review it, then use Save as New Map to keep it.' });
    } catch (error) { setMessage({ type: 'error', text: error.message }); } finally { setBusy(false); setGenerating(false); }
  };
  // Saves if needed, then redraws everything on the draft's painted ground. No image request.
  const redraw = async () => {
    setBusy(true); setMessage(null);
    try {
      const revision = dirty || !draft.revision ? await persist() : draft.revision;
      const response = await apiFetch(`/campaigns/${encodedCampaign}/map-drafts/${encodeURIComponent(draft.draftId)}/redraw`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expectedRevision: revision }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'The map could not be redrawn.');
      await finishMap(body);
      setMessage({ type: 'ok', text: 'Map redrawn on its ground. Review it, then use Save as New Map to keep it.' });
    } catch (error) { setMessage({ type: 'error', text: error.message }); } finally { setBusy(false); }
  };
  const open = async (draftId) => {
    if (!draftId) return;
    setBusy(true); setMessage(null);
    try {
      const response = await apiFetch(`/campaigns/${encodedCampaign}/map-drafts/${encodeURIComponent(draftId)}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'The draft could not be opened.');
      resetEditor({ draftId: body.draftId, name: body.name, revision: body.revision, layout: normalizeLayout(body.layout), groundSignature: body.groundSignature });
      setSelectedPinId(null); setSelectedStickerId(null); setDirty(false);
    } catch (error) { setMessage({ type: 'error', text: error.message }); } finally { setBusy(false); }
  };
  const startNew = () => {
    resetEditor({ draftId: newDraftId(), name: 'Untitled map', revision: 0, layout: emptyLayout(layout.shape || DEFAULT_MAP_SHAPE) });
    setSelectedPinId(null); setSelectedStickerId(null); setDirty(false); setMessage(null);
  };
  const remove = async () => {
    if (!draft.revision) { startNew(); return; }
    setBusy(true);
    try {
      const response = await apiFetch(`/campaigns/${encodedCampaign}/map-drafts/${encodeURIComponent(draft.draftId)}`, { method: 'DELETE' });
      if (!response.ok) throw new Error((await response.json()).message || 'The draft could not be deleted.');
      startNew(); loadDrafts(); setMessage({ type: 'ok', text: 'Draft deleted.' });
    } catch (error) { setMessage({ type: 'error', text: error.message }); } finally { setBusy(false); }
  };

  const activeTool = TOOLS.find((entry) => entry.id === tool);
  const groundFits = Boolean(draft.groundSignature) && draft.groundSignature === groundSignature(layout);
  const { columns, rows } = layout;
  const wallLine = (key, kind, preview = false) => {
    const [x, y, dir] = key.split(',');
    const x1 = Number(x); const y1 = Number(y);
    return <line key={`${preview ? 'p' : 'w'}-${key}`} x1={x1} y1={y1} x2={dir === 'h' ? x1 + 1 : x1} y2={dir === 'v' ? y1 + 1 : y1}
      data-wall-style={layout.wallStyles?.[key]}
      className={classNames('map-maker__wall', `map-maker__wall--${kind}`, preview && 'map-maker__wall--preview')} stroke={kind === 'remove' ? '#ef4444' : WALL_KINDS[kind]?.color} />;
  };

  return (
    <div className="map-maker">
      <div className="map-maker__toolbar" role="toolbar" aria-label="Map maker tools">
        {TOOLS.map((entry) => (
          <Button key={entry.id} size="sm" variant={tool === entry.id ? 'light' : 'outline-light'} aria-pressed={tool === entry.id} title={entry.hint}
            onClick={() => setTool(entry.id)}>{entry.label}</Button>
        ))}
        <span className="map-maker__divider" aria-hidden="true" />
        <Button size="sm" variant="outline-light" onClick={undo} disabled={!editor.past.length} title="Undo (Ctrl+Z)">Undo</Button>
        <Button size="sm" variant="outline-light" onClick={redo} disabled={!editor.future.length} title="Redo (Ctrl+Y)">Redo</Button>
      </div>

      <div className="map-maker__options" aria-label={`${activeTool.label} options`}>
        <p className="map-maker__hint">{placingStickers ? STICKER_HINT : activeTool.hint}</p>
        {(tool === 'paint' || tool === 'fill') && (
          <div className="map-maker__swatches" role="radiogroup" aria-label="Terrain">
            {Object.values(TERRAIN).map((entry) => (
              <button key={entry.id} type="button" role="radio" aria-checked={terrainId === entry.id} title={entry.label}
                className={classNames('map-maker__swatch', terrainId === entry.id && 'map-maker__swatch--active')} onClick={() => setTerrainId(entry.id)}>
                <span className="map-maker__swatch-color" style={{ background: entry.color }} />{entry.label}
              </button>
            ))}
          </div>
        )}
        {(tool === 'paint' || tool === 'erase') && (
          <Form.Group className="map-maker__inline" controlId="map-maker-brush">
            <Form.Label>Brush</Form.Label>
            <Form.Select size="sm" value={brushSize} onChange={(event) => setBrushSize(Number(event.target.value))}>
              <option value={1}>1 square</option><option value={2}>2 × 2</option><option value={3}>3 × 3</option>
            </Form.Select>
          </Form.Group>
        )}
        {tool === 'wall' && (
          <div className="map-maker__swatches" role="radiogroup" aria-label="Wall type">
            {[...Object.values(WALL_KINDS), { id: 'remove', label: 'Remove walls', color: '#ef4444' }].map((entry) => (
              <button key={entry.id} type="button" role="radio" aria-checked={wallKind === entry.id}
                title={entry.cover ? `${COVER_LABELS[entry.cover]}${entry.blocksMovement ? ', blocks movement' : entry.hurdle ? ', 5 extra feet to cross' : ''}` : 'Drag along walls to remove them'}
                className={classNames('map-maker__swatch', wallKind === entry.id && 'map-maker__swatch--active')} onClick={() => setWallKind(entry.id)}>
                <span className="map-maker__swatch-line" style={{ background: entry.color }} />{entry.label}
              </button>
            ))}
          </div>
        )}
        {tool === 'wall' && wallKind !== 'remove' && WALL_STYLES.length > 0 && (
          <div className="map-maker__swatches" role="radiogroup" aria-label="Wall style">
            {WALL_STYLES.map((style) => (
              <button key={style} type="button" role="radio" aria-checked={wallStyle === style}
                className={classNames('map-maker__swatch', wallStyle === style && 'map-maker__swatch--active')} onClick={() => setWallStyle(style)}>
                {style}
              </button>
            ))}
          </div>
        )}
        {stamping && <MapStampPicker key={tool} tool={tool} value={objectId} onChange={setObjectId} />}
        {stamping && MAP_OBJECTS[objectId]?.sizes && (
          <div className="map-maker__swatches" role="radiogroup" aria-label="Stamp size">
            {MAP_OBJECTS[objectId].sizes.map((size) => (
              <button key={size} type="button" role="radio" aria-checked={objectSize === size}
                className={classNames('map-maker__swatch', objectSize === size && 'map-maker__swatch--active')} onClick={() => setObjectSize(size)}>
                {size} × {size}
              </button>
            ))}
          </div>
        )}
        {selectedSticker && (
          <div className="map-maker__sticker-controls">
            {/* The angle goes after the slider, in a box of its own width: text before it that grew with the number would
                move the slider under the pointer mid-drag. */}
            <label htmlFor="map-maker-sticker-turn">{STICKERS[selectedSticker.kind].label} turn</label>
            <Form.Range id="map-maker-sticker-turn" aria-label="Sticker turn" min={0} max={359} value={selectedSticker.turn}
              onChange={(event) => turnSticker(selectedSticker.id, Number(event.target.value))} />
            <output htmlFor="map-maker-sticker-turn" className="map-maker__sticker-angle">{selectedSticker.turn}°</output>
            <Button size="sm" variant="outline-light" aria-label={`Turn left ${STICKER_TURN_STEP} degrees`} onClick={() => turnStickerBy(selectedSticker.id, -STICKER_TURN_STEP)}>↺</Button>
            <Button size="sm" variant="outline-light" aria-label={`Turn right ${STICKER_TURN_STEP} degrees`} onClick={() => turnStickerBy(selectedSticker.id, STICKER_TURN_STEP)}>↻</Button>
            <Button size="sm" variant="outline-light" onClick={() => nextPicture(selectedSticker)}>Next picture</Button>
            <Button size="sm" variant="outline-danger" onClick={() => deleteSticker(selectedSticker.id)}>Remove sticker</Button>
          </div>
        )}
        {stamping && chosenStamp?.facings && (
          <div className="map-maker__swatches" role="radiogroup" aria-label={chosenStamp.facings}>
            {Object.entries(MAP_FACINGS).map(([id, { label, arrow }]) => (
              <button key={id} type="button" role="radio" aria-checked={facing === id} title={`${chosenStamp.facings}: ${label.toLowerCase()}`}
                className={classNames('map-maker__swatch', facing === id && 'map-maker__swatch--active')} onClick={() => setFacing(id)}>
                {arrow} {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="map-maker__body">
        {/* The stage area fills the space left in the window; the grid is the largest box of its shape that fits. */}
        <div className="map-maker__stage-area">
        <div className="map-maker__stage" style={{ aspectRatio: `${columns} / ${rows}`, '--mm-ratio': columns / rows }}>
          <svg ref={svgRef} className={`map-maker__canvas map-maker__canvas--${tool}`} viewBox={`0 0 ${columns} ${rows}`} preserveAspectRatio="none"
            role="img" aria-label={`Map layout, ${columns} by ${rows} squares`} data-testid="map-maker-canvas"
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}>
            <rect x="0" y="0" width={columns} height={rows} className="map-maker__ground" />
            {Object.entries(layout.terrain).map(([key, id]) => {
              const [x, y] = key.split(',').map(Number);
              return <rect key={`t-${key}`} x={x} y={y} width="1" height="1" fill={TERRAIN[id]?.color} data-terrain={id} />;
            })}
            <g className="map-maker__grid" aria-hidden="true">
              {Array.from({ length: columns + 1 }, (_, x) => <line key={`gx-${x}`} x1={x} y1="0" x2={x} y2={rows} />)}
              {Array.from({ length: rows + 1 }, (_, y) => <line key={`gy-${y}`} x1="0" y1={y} x2={columns} y2={y} />)}
            </g>
            {Object.entries(layout.objectSizes || {}).map(([key, size]) => {
              const [x, y] = key.split(',').map(Number);
              return <rect key={`span-${key}`} data-object-span={key} x={Math.min(x, columns - size)} y={Math.min(y, rows - size)} width={size} height={size} fill="none" stroke={objectSketchColor(layout.objects[key])} strokeWidth="0.08" strokeDasharray="0.25 0.15" />;
            })}
            {Object.entries(layout.openTents || {}).map(([key, { id, facing: door }]) => {
              const [x, y] = key.split(',').map(Number);
              return (
                <g key={`tent-${key}`} className="map-maker__object" data-open-tent={id}>
                  <rect x={x} y={y} width={OPEN_TENT_SPAN} height={OPEN_TENT_SPAN} fill="none" stroke={objectSketchColor(id)} strokeWidth="0.08" strokeDasharray="0.25 0.15" />
                  <text data-stamp-facing={key} x={x + OPEN_TENT_SPAN / 2} y={y + OPEN_TENT_SPAN / 2 + 0.02} dominantBaseline="middle" textAnchor="middle">{MAP_FACINGS[door].arrow}</text>
                </g>
              );
            })}
            {/* Touching stamps of one kind show as one outlined patch with one letter, as the generator sees them. */}
            {objectGroups(layout).map((group) => {
              const inGroup = new Set(group.cells.map((cell) => cellKey(cell.x, cell.y)));
              const outline = group.cells.flatMap(({ x, y }) => [
                !inGroup.has(cellKey(x, y - 1)) && `M${x} ${y}H${x + 1}`, !inGroup.has(cellKey(x, y + 1)) && `M${x} ${y + 1}H${x + 1}`,
                !inGroup.has(cellKey(x - 1, y)) && `M${x} ${y}V${y + 1}`, !inGroup.has(cellKey(x + 1, y)) && `M${x + 1} ${y}V${y + 1}`,
              ]).filter(Boolean).join('');
              const midX = group.cells.reduce((sum, cell) => sum + cell.x + 0.5, 0) / group.cells.length;
              const midY = group.cells.reduce((sum, cell) => sum + cell.y + 0.5, 0) / group.cells.length;
              const anchor = group.cells.reduce((best, cell) => (Math.hypot(cell.x + 0.5 - midX, cell.y + 0.5 - midY) < Math.hypot(best.x + 0.5 - midX, best.y + 0.5 - midY) ? cell : best));
              const solid = group.cells.length === (group.right - group.left + 1) * (group.bottom - group.top + 1);
              return (
                <g key={`o-${group.id}-${group.left},${group.top}`} className="map-maker__object" data-object-group={group.id}>
                  {group.cells.map(({ x, y }) => <rect key={cellKey(x, y)} x={x} y={y} width="1" height="1" fill={objectSketchColor(group.id)} data-object={group.id} />)}
                  <path d={outline} className="map-maker__object-outline" />
                  {MAP_OBJECTS[group.id].facings
                    ? group.cells.map(({ x, y }) => {
                      const trail = MAP_OBJECTS[group.id].trail;
                      const turn = trail ? `rotate(${Math.round((trailStep(layout, x, y).turn * 180) / Math.PI)} ${x + 0.5} ${y + 0.52})` : undefined;
                      return <text key={`f-${x},${y}`} data-stamp-facing={cellKey(x, y)} transform={turn} x={x + 0.5} y={y + 0.52} dominantBaseline="middle" textAnchor="middle">{trail ? MAP_FACINGS.north.arrow : MAP_FACINGS[objectFacing(layout, x, y)].arrow}</text>;
                    })
                    : <text x={solid ? midX : anchor.x + 0.5} y={(solid ? midY : anchor.y + 0.5) + 0.02} dominantBaseline="middle" textAnchor="middle">{MAP_OBJECTS[group.id].symbol}</text>}
                </g>
              );
            })}
            {/* Stickers lie anywhere, at any angle, with their picture on their outline and an arrow at the front. */}
            {(layout.stickers || []).map((sticker) => {
              const { width, length, cover, fit } = STICKERS[sticker.kind];
              const pictures = MAP_ART[stickerArtId(sticker.kind)]?.plain || [];
              const [left, top] = [sticker.x - width / 2, sticker.y - length / 2];
              return (
                <g key={sticker.id} data-sticker={sticker.kind} data-sticker-at={`${sticker.x},${sticker.y}`} data-sticker-turn={sticker.turn} data-sticker-art={sticker.art}
                  className={classNames('map-maker__sticker', sticker.id === selectedSticker?.id && 'map-maker__sticker--selected')}
                  transform={`rotate(${sticker.turn} ${sticker.x} ${sticker.y})`}>
                  <rect x={left} y={top} width={width} height={length} rx="0.15" fill={SKETCH_STYLE.cover[cover || 'difficult']} />
                  {pictures.length > 0 && <image href={pictures[sticker.art % pictures.length]} x={left} y={top} width={width} height={length} preserveAspectRatio={fit === 'contain' ? 'xMidYMid meet' : 'none'} />}
                  <text x={sticker.x} y={top + 0.4} dominantBaseline="middle" textAnchor="middle">{MAP_FACINGS.north.arrow}</text>
                </g>
              );
            })}
            {Object.entries(layout.walls).map(([key, kind]) => wallLine(key, kind))}
            {previewEdges.map((key) => wallLine(key, wallKind, true))}
            {layout.pins.map((pin, index) => (
              <g key={pin.id} className={classNames('map-maker__pin', pin.id === selectedPinId && 'map-maker__pin--selected')} data-pin={index + 1}
                onPointerDown={(event) => { if (tool !== 'pin') return; event.stopPropagation(); setSelectedPinId(pin.id); }}>
                <circle cx={pin.x} cy={pin.y} r="0.42" />
                <text x={pin.x} y={pin.y + 0.02} dominantBaseline="middle" textAnchor="middle">{index + 1}</text>
              </g>
            ))}
          </svg>
        </div>
        </div>

        <aside className="map-maker__panel">
          <Form.Group controlId="map-maker-name">
            <Form.Label>Draft name</Form.Label>
            <Form.Control size="sm" value={draft.name} maxLength={LAYOUT_LIMITS.nameLength}
              onChange={(event) => { const name = event.target.value; setDraft((current) => ({ ...current, name })); setDirty(true); }} />
          </Form.Group>
          <Form.Group controlId="map-maker-shape">
            <Form.Label>Grid</Form.Label>
            <Form.Select size="sm" value={layout.shape} onChange={(event) => setShape(event.target.value)}>
              {Object.entries(MAP_SHAPES).map(([id, shape]) => <option key={id} value={id}>{shape.label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="map-maker-scene">
            <Form.Label>Scene description</Form.Label>
            <Form.Control as="textarea" rows={3} size="sm" maxLength={LAYOUT_LIMITS.sceneLength} value={layout.scene}
              placeholder="What is this place? Setting, mood, weather…"
              onChange={(event) => { const scene = event.target.value; change((current) => ({ ...current, scene }), { record: false }); }} />
          </Form.Group>
          <div className="map-maker__lighting">
            <span className="form-label" id="map-maker-lighting-label">Time of day</span>
            <div role="group" aria-labelledby="map-maker-lighting-label">
              {Object.entries(MAP_LIGHTING).map(([id, lighting]) => (
                <Button key={id} size="sm" variant={layout.lighting === id ? 'light' : 'outline-light'} aria-pressed={layout.lighting === id}
                  onClick={() => change((current) => ({ ...current, lighting: id }))}>{lighting.label}</Button>
              ))}
            </div>
          </div>

          <div className="map-maker__pins">
            <h3>Pins</h3>
            {layout.pins.length === 0 ? <p className="map-maker__empty">Use the Pins tool to mark things for the generator, like “old stone well”.</p> : (
              <ol>
                {layout.pins.map((pin) => (
                  <li key={pin.id} className={classNames(pin.id === selectedPinId && 'map-maker__pin-row--selected')}>
                    <button type="button" className="map-maker__pin-select" onClick={() => setSelectedPinId(pin.id)}>
                      {pin.note || <em>No note yet</em>}
                    </button>
                  </li>
                ))}
              </ol>
            )}
            {selectedPin && (
              <div className="map-maker__pin-editor">
                <Form.Label htmlFor="map-maker-pin-note">Pin {pinNumber(selectedPin.id)} note</Form.Label>
                <Form.Control id="map-maker-pin-note" as="textarea" rows={2} size="sm" maxLength={LAYOUT_LIMITS.noteLength} value={selectedPin.note} autoFocus
                  placeholder="What is here?" onChange={(event) => updatePin(selectedPin.id, { note: event.target.value })} />
                <Button size="sm" variant="outline-danger" onClick={() => removePin(selectedPin.id)}>Delete pin</Button>
              </div>
            )}
          </div>

          <div className="map-maker__drafts">
            <Form.Group controlId="map-maker-open">
              <Form.Label>Open draft</Form.Label>
              <Form.Select size="sm" value="" onChange={(event) => open(event.target.value)} disabled={busy || !drafts.length}>
                <option value="">{drafts.length ? 'Choose a saved draft…' : 'No saved drafts yet'}</option>
                {drafts.map((entry) => <option key={entry.draftId} value={entry.draftId}>{entry.name}</option>)}
              </Form.Select>
            </Form.Group>
            <div className="map-maker__generate">
              <Button variant="warning" onClick={generate} disabled={busy || !onGenerated}>
                {generating ? <><Spinner size="sm" animation="border" className="me-2" />Painting the map…</> : 'Generate map from layout'}
              </Button>
              {groundFits && <Button variant="outline-warning" onClick={redraw} disabled={busy || !onGenerated}>Redraw on this ground (free)</Button>}
              <p>Saves the draft, then has the AI image generator paint the ground and your notes (one image request). Roofs, cover and walls go on top, exactly on their squares.{groundFits ? ' The ground still fits this layout, so a redraw needs no image.' : ''}</p>
            </div>
            <div className="map-maker__actions">
              <Button size="sm" variant="primary" onClick={save} disabled={busy}>{busy && !generating ? <Spinner size="sm" animation="border" /> : 'Save draft'}</Button>
              <Button size="sm" variant="outline-light" onClick={startNew} disabled={busy}>New</Button>
              <Button size="sm" variant="outline-danger" onClick={remove} disabled={busy}>Delete</Button>
              {onClose && <Button size="sm" variant="outline-light" onClick={onClose}>Close</Button>}
            </div>
            <p className={classNames('map-maker__status', message?.type === 'error' && 'map-maker__status--error')} role="status">
              {message?.text || (dirty ? 'Unsaved changes.' : draft.revision ? 'All changes saved.' : 'Not saved yet.')}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

MapMaker.propTypes = {
  campaignId: PropTypes.string,
  onClose: PropTypes.func,
  // Receives the generated map (image, grid size and layout) for the DM to review and save.
  onGenerated: PropTypes.func,
};
